/**
 * LA VOZ DE MARTY — microcopy del producto, por seccion y por estado.
 *
 * Fuente de verdad: «Brandbook Marty» v1.0, laminas 07 (tono de voz) y 25
 * (microcopy del producto). Las reglas, textuales:
 *
 *   PRIMERA PERSONA.      Marty habla, no «el sistema».
 *   SIGUIENTE PASO.       Nunca un problema sin su plan.
 *   SIN ALARMAS.          Las alertas informan; no gritan ni usan mayusculas.
 *   FRASES CORTAS.        Una idea por frase. Cuanto mas grave, mas corta.
 *   HUMOR SECO.           Un guino en la actitud, nunca un chiste.
 *
 * Y el limite que no se cruza: el humor nunca sugiere esconder, maquillar ni
 * «arreglar» numeros. La calma es ante el cierre, la auditoria y el vencimiento
 * —hechos reales del trabajo—, no ante una irregularidad.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTO ES UN MODULO Y NO TEXTO EN CADA PANTALLA
 *
 * Hay veinticuatro ambitos en el producto y cuatro estados en cada uno. Escritos
 * en la pantalla que los usa, son noventa y seis cadenas imposibles de revisar
 * de una vez, en tres idiomas, y la primera que se desvia del tono no la ve
 * nadie. Aca se leen todas juntas, se pueden probar sin montar React, y el dia
 * que cambie el tono cambia en un archivo.
 *
 * ---------------------------------------------------------------------------
 * LOS NOMBRES DEL FISCO NO SE TRADUCEN Y NO SE INVENTAN
 *
 * El brandbook: «Los nombres de entidades fiscales se localizan por mercado
 * (SUNAT, SAT, AFIP, IRS, Receita Federal); la frase no cambia». Por eso las
 * plantillas llevan `{autoridad}` y lo resuelve `resolver()` con el dato que ya
 * tiene `useI18n().term.autoridad`. Escribir «SUNAT» a mano en una frase la
 * volveria falsa en la sociedad de Sao Paulo.
 */

export type Idioma = "es" | "pt" | "en"

/**
 * Los ambitos del producto. Uno por pantalla con identidad propia, no uno por
 * ruta: `/contabilidad/libros/diario` y `/contabilidad/libros/mayor` son dos
 * trabajos distintos y dicen cosas distintas, pero las seis pantallas de
 * maestros son la misma tarea.
 */
export type Ambito =
  | "generico"
  | "dashboard"
  | "primeros_pasos"
  | "finanzas_pnl"
  | "finanzas_caja"
  | "finanzas_revenue"
  | "finanzas_costo_ventas"
  | "finanzas_gastos"
  | "finanzas_payroll"
  | "finanzas_conciliacion"
  | "finanzas_hoja_vs_libro"
  | "revision"
  | "libro_diario"
  | "libro_mayor"
  | "libro_balance"
  | "libro_analisis"
  | "libro_comparar"
  | "estado_situacion"
  | "estado_resultados"
  | "estado_flujo"
  | "maestros"
  | "reglas"
  | "declaraciones"
  | "holding_consolidado"
  | "holding_intercompania"
  | "holding_tesoreria"
  | "holding_estructura"
  | "holding_obligaciones"
  | "auditoria"

export interface Vacio {
  /** Que NO hay. Nunca «sin datos»: se dice que falta. */
  titulo: string
  /** Como seguir. Un vacio sin salida es un callejon. */
  pista: string
}

export interface Voz {
  /** Mientras se consulta. Va junto al esqueleto, no en lugar de el. */
  carga: string
  vacio: Vacio
  /** Cabecera del error. El QUE y el COMO seguir los da `describeError`. */
  error: string
}

/* ===========================================================================
   ESPANOL — el catalogo de referencia.
   Espanol neutro latinoamericano: «tu», nunca «vos», como fija el brandbook.
   =========================================================================== */

const ES: Record<Ambito, Voz> = {
  generico: {
    carga: "Marty está haciendo cuadrar todo…",
    vacio: {
      titulo: "Todavía no hay números aquí.",
      pista: "Marty está listo cuando tú lo estés.",
    },
    error: "Algo no cuadra aquí. Marty ya lo encontró.",
  },

  dashboard: {
    carga: "Marty está juntando el mes…",
    vacio: {
      titulo: "Todavía no hay mes que mostrar.",
      pista: "Carga tus comprobantes y Marty arma el tablero solo.",
    },
    error: "El tablero no cargó. Los números están bien; la consulta, no.",
  },

  primeros_pasos: {
    carga: "Marty está revisando qué trajiste…",
    vacio: {
      titulo: "Aún no hay nada del histórico.",
      pista: "Empieza por el primer paso. Marty va marcando lo que ya está.",
    },
    error: "No pude leer el avance del traspaso. Vuelve a intentar.",
  },

  /* ------------------------------------------------------------- Finanzas */

  finanzas_pnl: {
    carga: "Marty está armando el resultado…",
    vacio: {
      titulo: "No hay resultado para este período.",
      pista: "Falta cargar ventas o gastos del mes. Empieza por Conciliación.",
    },
    error: "No pude armar el P&L. El libro está intacto.",
  },

  finanzas_caja: {
    carga: "Marty está contando la caja…",
    vacio: {
      titulo: "No hay movimiento de caja en este período.",
      pista: "La caja se arma con los cobros y pagos conciliados.",
    },
    error: "No pude calcular la caja. Reintenta la consulta.",
  },

  finanzas_revenue: {
    carga: "Marty está sumando lo facturado…",
    vacio: {
      titulo: "No hay ingresos registrados en este período.",
      pista: "Revisa los contratos activos y sus líneas vigentes.",
    },
    error: "No pude traer los ingresos. Nada se perdió.",
  },

  finanzas_costo_ventas: {
    carga: "Marty está calculando el costo…",
    vacio: {
      titulo: "No hay costo de ventas en este período.",
      pista: "El costo sale de la gente y los gastos clasificados como directos.",
    },
    error: "No pude calcular el costo de ventas. Reintenta.",
  },

  finanzas_gastos: {
    carga: "Marty está ordenando los gastos…",
    vacio: {
      titulo: "No hay gastos operativos en este período.",
      pista: "Si esperabas ver gastos, revisa la cola de revisión: puede que estén sin clasificar.",
    },
    error: "No pude traer los gastos. El libro está intacto.",
  },

  finanzas_payroll: {
    carga: "Marty está cerrando la planilla…",
    vacio: {
      titulo: "No hay planilla en este período.",
      pista: "Da de alta a tu gente en Maestros y Marty calcula el costo laboral.",
    },
    error: "No pude calcular la planilla. Ningún pago se movió.",
  },

  finanzas_conciliacion: {
    carga: "Marty está cruzando el banco con el libro…",
    vacio: {
      titulo: "No hay nada por conciliar.",
      pista: "Buena noticia: todo lo cargado ya tiene su lugar.",
    },
    error: "No pude traer la conciliación. Reintenta.",
  },

  finanzas_hoja_vs_libro: {
    carga: "Marty está comparando Finanzas con Contabilidad…",
    vacio: {
      titulo: "No hay diferencias que mostrar.",
      pista: "Finanzas y Contabilidad dicen lo mismo. Así debe ser.",
    },
    error: "No pude comparar. Cada fuente sigue intacta por separado.",
  },

  /* --------------------------------------------------------- Contabilidad */

  revision: {
    carga: "Marty está revisando los comprobantes…",
    vacio: {
      titulo: "No hay nada esperando tu revisión.",
      pista: "Marty resolvió todo lo que llegó. Puedes respirar.",
    },
    error: "No pude traer la cola. Ningún comprobante cambió de estado.",
  },

  libro_diario: {
    carga: "Marty está pasando las páginas del diario…",
    vacio: {
      titulo: "No hay asientos en este período.",
      pista: "Los asientos se crean al clasificar un comprobante, o a mano desde un ajuste.",
    },
    error: "No pude abrir el diario. Los asientos están donde los dejaste.",
  },

  libro_mayor: {
    carga: "Marty está sumando el mayor…",
    vacio: {
      titulo: "Esta cuenta no tiene movimiento en el período.",
      pista: "Prueba con otro período, u otra cuenta del plan.",
    },
    error: "No pude abrir el mayor. Reintenta la consulta.",
  },

  libro_balance: {
    carga: "Marty está cuadrando el balance…",
    vacio: {
      titulo: "No hay saldos en este período.",
      pista: "Sin asientos no hay balance. Empieza por la cola de revisión.",
    },
    error: "No pude calcular el balance. Ningún saldo se alteró.",
  },

  libro_analisis: {
    carga: "Marty está analizando la cuenta…",
    vacio: {
      titulo: "Elige una cuenta para analizarla.",
      pista: "Marty te muestra su movimiento, su saldo y de dónde viene cada línea.",
    },
    error: "No pude analizar la cuenta. Reintenta.",
  },

  libro_comparar: {
    carga: "Marty está poniendo los tres libros uno al lado del otro…",
    vacio: {
      titulo: "No hay diferencias entre los libros.",
      pista: "Contable, tributario y gerencial coinciden en este período.",
    },
    error: "No pude comparar los libros. Cada uno sigue intacto.",
  },

  estado_situacion: {
    carga: "Marty está armando el balance general…",
    vacio: {
      titulo: "No hay estado de situación para este período.",
      pista: "Se construye desde los asientos: sin asientos, no hay estado.",
    },
    error: "No pude armar el estado de situación. El libro está intacto.",
  },

  estado_resultados: {
    carga: "Marty está armando el estado de resultados…",
    vacio: {
      titulo: "No hay estado de resultados para este período.",
      pista: "Se construye desde los asientos del período. Revisa que estén contabilizados.",
    },
    error: "No pude armar el estado de resultados. Reintenta.",
  },

  estado_flujo: {
    carga: "Marty está armando el flujo de efectivo…",
    vacio: {
      titulo: "No hay flujo de efectivo para este período.",
      pista: "El flujo sale de las cuentas de caja y bancos del libro.",
    },
    error: "No pude armar el flujo. Ningún saldo cambió.",
  },

  maestros: {
    carga: "Marty está abriendo la ficha…",
    vacio: {
      titulo: "Aún no hay nada dado de alta aquí.",
      pista: "Crea el primero y Marty lo usa desde el siguiente comprobante.",
    },
    error: "No pude traer el maestro. Nada se modificó.",
  },

  reglas: {
    carga: "Marty está leyendo tus reglas…",
    vacio: {
      titulo: "No hay reglas de tratamiento todavía.",
      pista: "Sin reglas propias Marty aplica la norma del país. Una regla solo cambia lo que tú decidas cambiar.",
    },
    error: "No pude leer las reglas. Las que ya existen siguen aplicándose.",
  },

  declaraciones: {
    carga: "Marty está armando la declaración…",
    vacio: {
      titulo: "No hay declaraciones en este período.",
      pista: "Marty las prepara al cerrar el mes. Tú revisas y firmas.",
    },
    error: "No pude traer las declaraciones. Ninguna se presentó ni se alteró.",
  },

  /* ------------------------------------------------------------- El grupo */

  holding_consolidado: {
    carga: "Marty está juntando las cuatro sociedades…",
    vacio: {
      titulo: "No hay nada consolidado en este período.",
      pista: "Ninguna sociedad tiene asientos en el libro elegido. El consolidado sale de los asientos, no de las facturas.",
    },
    error: "No pude armar el consolidado. Los libros de cada sociedad están intactos.",
  },
  holding_intercompania: {
    carga: "Marty está buscando lo que se facturan entre ustedes…",
    vacio: {
      titulo: "No hay operaciones entre tus sociedades.",
      pista: "O no las hay, o el comprobante llegó sin identificar a la contraparte. Revisa las sugerencias.",
    },
    error: "No pude cruzar las operaciones entre sociedades. Nada se eliminó.",
  },
  holding_tesoreria: {
    carga: "Marty está contando la caja de las cuatro…",
    vacio: {
      titulo: "No hay cuentas bancarias cargadas.",
      pista: "La caja del grupo sale de los saldos bancarios. Cárgalos y yo los convierto y los sumo.",
    },
    error: "No pude calcular la caja del grupo. Ningún saldo cambió.",
  },
  auditoria: {
    carga: "Marty está abriendo el registro de cambios…",
    vacio: {
      titulo: "Nadie ha corregido nada con motivo en este alcance.",
      pista: "Es buena noticia: no hizo falta corregir a mano. Quita el filtro para ver también lo que cargaron los importadores.",
    },
    error: "No pude leer la pista. Nada de lo registrado se alteró: la tabla no se puede reescribir.",
  },
  holding_obligaciones: {
    carga: "Marty está revisando qué deben las cuatro…",
    vacio: {
      titulo: "No hay sociedades con normativa cargada.",
      pista: "Conozco las obligaciones de Perú, México, Brasil y Panamá. Una sociedad de otro país aparece vacía hasta que exista su normativa.",
    },
    error: "No pude armar el mapa de obligaciones. La normativa sigue donde estaba.",
  },
  holding_estructura: {
    carga: "Marty está leyendo quién posee qué…",
    vacio: {
      titulo: "No hay sociedades en este cliente.",
      pista: "Sin sociedades no hay grupo que consolidar.",
    },
    error: "No pude leer la estructura. Las participaciones declaradas siguen donde estaban.",
  },
}

/* ===========================================================================
   PORTUGUES — obligatorio para Brasil, no una cortesia.
   =========================================================================== */

const PT: Record<Ambito, Voz> = {
  generico: {
    carga: "O Marty está fazendo tudo fechar…",
    vacio: {
      titulo: "Ainda não há números aqui.",
      pista: "O Marty está pronto quando você estiver.",
    },
    error: "Algo não fecha aqui. O Marty já encontrou.",
  },
  dashboard: {
    carga: "O Marty está juntando o mês…",
    vacio: {
      titulo: "Ainda não há mês para mostrar.",
      pista: "Carregue suas notas e o Marty monta o painel sozinho.",
    },
    error: "O painel não carregou. Os números estão certos; a consulta, não.",
  },
  primeros_pasos: {
    carga: "O Marty está vendo o que você trouxe…",
    vacio: {
      titulo: "Ainda não há nada do histórico.",
      pista: "Comece pelo primeiro passo. O Marty vai marcando o que já está feito.",
    },
    error: "Não consegui ler o andamento da migração. Tente de novo.",
  },
  finanzas_pnl: {
    carga: "O Marty está montando o resultado…",
    vacio: {
      titulo: "Não há resultado neste período.",
      pista: "Faltam receitas ou despesas do mês. Comece pela Conciliação.",
    },
    error: "Não consegui montar o resultado. O livro está intacto.",
  },
  finanzas_caja: {
    carga: "O Marty está contando o caixa…",
    vacio: {
      titulo: "Não há movimento de caixa neste período.",
      pista: "O caixa se monta com os recebimentos e pagamentos conciliados.",
    },
    error: "Não consegui calcular o caixa. Tente de novo.",
  },
  finanzas_revenue: {
    carga: "O Marty está somando o faturado…",
    vacio: {
      titulo: "Não há receita registrada neste período.",
      pista: "Confira os contratos ativos e suas linhas vigentes.",
    },
    error: "Não consegui trazer as receitas. Nada foi perdido.",
  },
  finanzas_costo_ventas: {
    carga: "O Marty está calculando o custo…",
    vacio: {
      titulo: "Não há custo dos serviços neste período.",
      pista: "O custo vem da equipe e das despesas classificadas como diretas.",
    },
    error: "Não consegui calcular o custo. Tente de novo.",
  },
  finanzas_gastos: {
    carga: "O Marty está organizando as despesas…",
    vacio: {
      titulo: "Não há despesas operacionais neste período.",
      pista: "Se você esperava ver despesas, confira a fila de revisão: podem estar sem classificar.",
    },
    error: "Não consegui trazer as despesas. O livro está intacto.",
  },
  finanzas_payroll: {
    carga: "O Marty está fechando a folha…",
    vacio: {
      titulo: "Não há folha neste período.",
      pista: "Cadastre sua equipe em Cadastros e o Marty calcula o custo de pessoal.",
    },
    error: "Não consegui calcular a folha. Nenhum pagamento se moveu.",
  },
  finanzas_conciliacion: {
    carga: "O Marty está cruzando o banco com o livro…",
    vacio: {
      titulo: "Não há nada a conciliar.",
      pista: "Boa notícia: tudo que entrou já tem seu lugar.",
    },
    error: "Não consegui trazer a conciliação. Tente de novo.",
  },
  finanzas_hoja_vs_libro: {
    carga: "O Marty está comparando Finanças com Contabilidade…",
    vacio: {
      titulo: "Não há diferenças para mostrar.",
      pista: "Finanças e Contabilidade dizem a mesma coisa. É assim que deve ser.",
    },
    error: "Não consegui comparar. Cada fonte segue intacta em separado.",
  },
  revision: {
    carga: "O Marty está revisando as notas…",
    vacio: {
      titulo: "Não há nada esperando sua revisão.",
      pista: "O Marty resolveu tudo que chegou. Você pode respirar.",
    },
    error: "Não consegui trazer a fila. Nenhuma nota mudou de estado.",
  },
  libro_diario: {
    carga: "O Marty está passando as páginas do diário…",
    vacio: {
      titulo: "Não há lançamentos neste período.",
      pista: "Os lançamentos nascem ao classificar uma nota, ou à mão por um ajuste.",
    },
    error: "Não consegui abrir o diário. Os lançamentos estão onde você deixou.",
  },
  libro_mayor: {
    carga: "O Marty está somando o razão…",
    vacio: {
      titulo: "Esta conta não tem movimento no período.",
      pista: "Tente outro período, ou outra conta do plano.",
    },
    error: "Não consegui abrir o razão. Tente de novo.",
  },
  libro_balance: {
    carga: "O Marty está fechando o balancete…",
    vacio: {
      titulo: "Não há saldos neste período.",
      pista: "Sem lançamentos não há balancete. Comece pela fila de revisão.",
    },
    error: "Não consegui calcular o balancete. Nenhum saldo foi alterado.",
  },
  libro_analisis: {
    carga: "O Marty está analisando a conta…",
    vacio: {
      titulo: "Escolha uma conta para analisar.",
      pista: "O Marty mostra o movimento, o saldo e de onde vem cada linha.",
    },
    error: "Não consegui analisar a conta. Tente de novo.",
  },
  libro_comparar: {
    carga: "O Marty está pondo os três livros lado a lado…",
    vacio: {
      titulo: "Não há diferenças entre os livros.",
      pista: "Contábil, fiscal e gerencial coincidem neste período.",
    },
    error: "Não consegui comparar os livros. Cada um segue intacto.",
  },
  estado_situacion: {
    carga: "O Marty está montando o balanço patrimonial…",
    vacio: {
      titulo: "Não há balanço patrimonial neste período.",
      pista: "Ele se constrói dos lançamentos: sem lançamentos, não há balanço.",
    },
    error: "Não consegui montar o balanço. O livro está intacto.",
  },
  estado_resultados: {
    carga: "O Marty está montando a demonstração do resultado…",
    vacio: {
      titulo: "Não há demonstração do resultado neste período.",
      pista: "Ela se constrói dos lançamentos do período. Confira se estão contabilizados.",
    },
    error: "Não consegui montar a demonstração. Tente de novo.",
  },
  estado_flujo: {
    carga: "O Marty está montando o fluxo de caixa…",
    vacio: {
      titulo: "Não há fluxo de caixa neste período.",
      pista: "O fluxo sai das contas de caixa e bancos do livro.",
    },
    error: "Não consegui montar o fluxo. Nenhum saldo mudou.",
  },
  maestros: {
    carga: "O Marty está abrindo o cadastro…",
    vacio: {
      titulo: "Ainda não há nada cadastrado aqui.",
      pista: "Crie o primeiro e o Marty usa a partir da próxima nota.",
    },
    error: "Não consegui trazer o cadastro. Nada foi modificado.",
  },
  reglas: {
    carga: "O Marty está lendo suas regras…",
    vacio: {
      titulo: "Ainda não há regras de tratamento.",
      pista: "Sem regras próprias o Marty aplica a norma do país. Uma regra só muda o que você decidir mudar.",
    },
    error: "Não consegui ler as regras. As que já existem seguem valendo.",
  },
  declaraciones: {
    carga: "O Marty está montando a obrigação…",
    vacio: {
      titulo: "Não há obrigações neste período.",
      pista: "O Marty as prepara no fechamento do mês. Você revisa e assina.",
    },
    error: "Não consegui trazer as obrigações. Nenhuma foi entregue nem alterada.",
  },

  holding_consolidado: {
    carga: "O Marty está juntando as quatro empresas…",
    vacio: {
      titulo: "Não há nada consolidado neste período.",
      pista: "Nenhuma empresa tem lançamentos no livro escolhido. O consolidado sai dos lançamentos, não das notas.",
    },
    error: "Não consegui montar o consolidado. Os livros de cada empresa estão intactos.",
  },
  holding_intercompania: {
    carga: "O Marty está procurando o que vocês faturam entre si…",
    vacio: {
      titulo: "Não há operações entre suas empresas.",
      pista: "Ou não existem, ou a nota chegou sem identificar a contraparte. Confira as sugestões.",
    },
    error: "Não consegui cruzar as operações entre empresas. Nada foi eliminado.",
  },
  holding_tesoreria: {
    carga: "O Marty está contando o caixa das quatro…",
    vacio: {
      titulo: "Não há contas bancárias cadastradas.",
      pista: "O caixa do grupo sai dos saldos bancários. Cadastre-os e eu converto e somo.",
    },
    error: "Não consegui calcular o caixa do grupo. Nenhum saldo mudou.",
  },
  auditoria: {
    carga: "O Marty está abrindo o registro de alterações…",
    vacio: {
      titulo: "Ninguém corrigiu nada com justificativa neste escopo.",
      pista: "É boa notícia: não foi preciso corrigir à mão. Tire o filtro para ver também o que os importadores carregaram.",
    },
    error: "Não consegui ler a trilha. Nada do registrado foi alterado: a tabela não pode ser reescrita.",
  },
  holding_obligaciones: {
    carga: "O Marty está vendo o que as quatro devem…",
    vacio: {
      titulo: "Não há empresas com normas carregadas.",
      pista: "Conheço as obrigações de Peru, México, Brasil e Panamá. Uma empresa de outro país aparece vazia até existir sua norma.",
    },
    error: "Não consegui montar o mapa de obrigações. As normas seguem onde estavam.",
  },
  holding_estructura: {
    carga: "O Marty está lendo quem detém o quê…",
    vacio: {
      titulo: "Não há empresas neste cliente.",
      pista: "Sem empresas não há grupo a consolidar.",
    },
    error: "Não consegui ler a estrutura. As participações declaradas seguem onde estavam.",
  },
}

/* ===========================================================================
   INGLES — para inversionistas y matrices. Mismo ritmo que el espanol.
   =========================================================================== */

const EN: Record<Ambito, Voz> = {
  generico: {
    carga: "Marty is making everything add up…",
    vacio: {
      titulo: "No numbers here yet.",
      pista: "Marty is ready when you are.",
    },
    error: "Something doesn't add up here. Marty already found it.",
  },
  dashboard: {
    carga: "Marty is pulling the month together…",
    vacio: {
      titulo: "No month to show yet.",
      pista: "Upload your documents and Marty builds the board on its own.",
    },
    error: "The board didn't load. The numbers are fine; the query wasn't.",
  },
  primeros_pasos: {
    carga: "Marty is checking what you brought over…",
    vacio: {
      titulo: "Nothing from your history yet.",
      pista: "Start with the first step. Marty ticks off what's already done.",
    },
    error: "I couldn't read the migration progress. Try again.",
  },
  finanzas_pnl: {
    carga: "Marty is building the P&L…",
    vacio: {
      titulo: "No result for this period.",
      pista: "Revenue or expenses are missing. Start with Reconciliation.",
    },
    error: "I couldn't build the P&L. The ledger is untouched.",
  },
  finanzas_caja: {
    carga: "Marty is counting the cash…",
    vacio: {
      titulo: "No cash movement in this period.",
      pista: "Cash is built from reconciled collections and payments.",
    },
    error: "I couldn't compute cash. Try again.",
  },
  finanzas_revenue: {
    carga: "Marty is adding up what was billed…",
    vacio: {
      titulo: "No revenue recorded in this period.",
      pista: "Check your active contracts and their current lines.",
    },
    error: "I couldn't fetch revenue. Nothing was lost.",
  },
  finanzas_costo_ventas: {
    carga: "Marty is working out the cost…",
    vacio: {
      titulo: "No cost of sales in this period.",
      pista: "Cost comes from your people and expenses classified as direct.",
    },
    error: "I couldn't compute cost of sales. Try again.",
  },
  finanzas_gastos: {
    carga: "Marty is sorting the expenses…",
    vacio: {
      titulo: "No operating expenses in this period.",
      pista: "If you expected expenses, check the review queue — they may be unclassified.",
    },
    error: "I couldn't fetch expenses. The ledger is untouched.",
  },
  finanzas_payroll: {
    carga: "Marty is closing payroll…",
    vacio: {
      titulo: "No payroll in this period.",
      pista: "Add your people under Masters and Marty computes the labour cost.",
    },
    error: "I couldn't compute payroll. No payment moved.",
  },
  finanzas_conciliacion: {
    carga: "Marty is matching the bank against the ledger…",
    vacio: {
      titulo: "Nothing left to reconcile.",
      pista: "Good news: everything loaded already has its place.",
    },
    error: "I couldn't fetch the reconciliation. Try again.",
  },
  finanzas_hoja_vs_libro: {
    carga: "Marty is comparing Finance against Accounting…",
    vacio: {
      titulo: "No differences to show.",
      pista: "Finance and Accounting say the same thing. That's how it should be.",
    },
    error: "I couldn't compare them. Each source is still intact on its own.",
  },
  revision: {
    carga: "Marty is reviewing the documents…",
    vacio: {
      titulo: "Nothing is waiting on your review.",
      pista: "Marty handled everything that came in. You can breathe.",
    },
    error: "I couldn't fetch the queue. No document changed state.",
  },
  libro_diario: {
    carga: "Marty is turning the pages of the journal…",
    vacio: {
      titulo: "No entries in this period.",
      pista: "Entries are created when a document is classified, or by hand from an adjustment.",
    },
    error: "I couldn't open the journal. Your entries are where you left them.",
  },
  libro_mayor: {
    carga: "Marty is adding up the ledger…",
    vacio: {
      titulo: "This account has no movement in the period.",
      pista: "Try another period, or another account in the chart.",
    },
    error: "I couldn't open the ledger. Try again.",
  },
  libro_balance: {
    carga: "Marty is balancing the trial balance…",
    vacio: {
      titulo: "No balances in this period.",
      pista: "No entries, no trial balance. Start with the review queue.",
    },
    error: "I couldn't compute the trial balance. No balance was altered.",
  },
  libro_analisis: {
    carga: "Marty is analysing the account…",
    vacio: {
      titulo: "Pick an account to analyse.",
      pista: "Marty shows its movement, its balance and where each line comes from.",
    },
    error: "I couldn't analyse the account. Try again.",
  },
  libro_comparar: {
    carga: "Marty is putting the three books side by side…",
    vacio: {
      titulo: "No differences between the books.",
      pista: "Statutory, tax and management agree in this period.",
    },
    error: "I couldn't compare the books. Each one is still intact.",
  },
  estado_situacion: {
    carga: "Marty is building the balance sheet…",
    vacio: {
      titulo: "No balance sheet for this period.",
      pista: "It's built from the entries: no entries, no statement.",
    },
    error: "I couldn't build the balance sheet. The ledger is untouched.",
  },
  estado_resultados: {
    carga: "Marty is building the income statement…",
    vacio: {
      titulo: "No income statement for this period.",
      pista: "It's built from the period's entries. Check that they are posted.",
    },
    error: "I couldn't build the income statement. Try again.",
  },
  estado_flujo: {
    carga: "Marty is building the cash flow statement…",
    vacio: {
      titulo: "No cash flow statement for this period.",
      pista: "The statement comes from the cash and bank accounts in the ledger.",
    },
    error: "I couldn't build the statement. No balance changed.",
  },
  maestros: {
    carga: "Marty is opening the record…",
    vacio: {
      titulo: "Nothing set up here yet.",
      pista: "Create the first one and Marty uses it from the next document on.",
    },
    error: "I couldn't fetch the master data. Nothing was modified.",
  },
  reglas: {
    carga: "Marty is reading your rules…",
    vacio: {
      titulo: "No treatment rules yet.",
      pista: "With no rules of your own, Marty applies the country's standard. A rule only changes what you decide to change.",
    },
    error: "I couldn't read the rules. The existing ones still apply.",
  },
  declaraciones: {
    carga: "Marty is preparing the filing…",
    vacio: {
      titulo: "No filings in this period.",
      pista: "Marty prepares them at month-end close. You review and sign.",
    },
    error: "I couldn't fetch the filings. None was submitted or altered.",
  },

  holding_consolidado: {
    carga: "Marty is pulling the four companies together…",
    vacio: {
      titulo: "Nothing consolidated in this period.",
      pista: "No company has entries in the chosen book. The consolidation comes from entries, not from invoices.",
    },
    error: "I couldn't build the consolidation. Each company's books are untouched.",
  },
  holding_intercompania: {
    carga: "Marty is looking for what you bill each other…",
    vacio: {
      titulo: "No transactions between your companies.",
      pista: "Either there are none, or the document arrived without identifying the counterparty. Check the suggestions.",
    },
    error: "I couldn't match transactions between companies. Nothing was eliminated.",
  },
  holding_tesoreria: {
    carga: "Marty is counting cash across all four…",
    vacio: {
      titulo: "No bank accounts loaded.",
      pista: "Group cash comes from bank balances. Load them and I'll convert and add them up.",
    },
    error: "I couldn't compute group cash. No balance changed.",
  },
  auditoria: {
    carga: "Marty is opening the change log…",
    vacio: {
      titulo: "Nobody has corrected anything with a reason in this scope.",
      pista: "That's good news: no manual correction was needed. Drop the filter to also see what the importers loaded.",
    },
    error: "I couldn't read the trail. Nothing on record changed: the table can't be rewritten.",
  },
  holding_obligaciones: {
    carga: "Marty is checking what all four owe…",
    vacio: {
      titulo: "No companies with rules loaded.",
      pista: "I know the obligations for Peru, Mexico, Brazil and Panama. A company elsewhere shows empty until its rules exist.",
    },
    error: "I couldn't build the obligations map. The rules are where they were.",
  },
  holding_estructura: {
    carga: "Marty is reading who owns what…",
    vacio: {
      titulo: "No companies for this client.",
      pista: "With no companies there is no group to consolidate.",
    },
    error: "I couldn't read the structure. Declared holdings are where they were.",
  },
}

const CATALOGO: Record<Idioma, Record<Ambito, Voz>> = { es: ES, pt: PT, en: EN }

/* ===========================================================================
   DE UNA RUTA A UN AMBITO
   =========================================================================== */

/**
 * El orden IMPORTA: se devuelve la primera coincidencia, asi que los prefijos
 * mas especificos van primero. `/contabilidad/libros/diario` tiene que caer en
 * `libro_diario` y no en el `/contabilidad` de mas abajo.
 */
const RUTAS: Array<[string, Ambito]> = [
  ["/finanzas/pnl", "finanzas_pnl"],
  ["/finanzas/caja", "finanzas_caja"],
  ["/finanzas/desglose", "finanzas_revenue"],
  ["/finanzas/revenue", "finanzas_revenue"],
  ["/finanzas/costo-ventas", "finanzas_costo_ventas"],
  ["/finanzas/gastos", "finanzas_gastos"],
  ["/finanzas/payroll", "finanzas_payroll"],
  /*
    `/finanzas/cuadres` monta LAS DOS comparaciones (punto 8 de la cuarta
    tanda). La nota que se enseña es la de la conciliación —la de arriba— porque
    es la primera que se lee al entrar; la del libro ya va en el encabezado de su
    propia mitad. Las dos rutas viejas se quedan: redirigen, y sin entrada aquí
    la nota desaparecería durante el salto.
  */
  ["/finanzas/cuadres", "finanzas_conciliacion"],
  ["/finanzas/conciliacion", "finanzas_conciliacion"],
  ["/finanzas/hoja-vs-libro", "finanzas_hoja_vs_libro"],
  ["/finanzas", "finanzas_pnl"],

  ["/contabilidad/revision", "revision"],
  ["/contabilidad/libros/diario", "libro_diario"],
  ["/contabilidad/libros/mayor", "libro_mayor"],
  ["/contabilidad/libros/balance", "libro_balance"],
  ["/contabilidad/libros/analisis", "libro_analisis"],
  ["/contabilidad/libros/comparar", "libro_comparar"],
  ["/contabilidad/libros", "libro_diario"],
  ["/contabilidad/estados/situacion", "estado_situacion"],
  ["/contabilidad/estados/resultados", "estado_resultados"],
  ["/contabilidad/estados/flujo", "estado_flujo"],
  ["/contabilidad/estados", "estado_situacion"],
  ["/contabilidad/maestros/reglas", "reglas"],
  ["/contabilidad/maestros", "maestros"],
  ["/contabilidad/declaraciones", "declaraciones"],
  ["/contabilidad", "revision"],

  ["/holding/consolidado", "holding_consolidado"],
  ["/holding/intercompania", "holding_intercompania"],
  ["/holding/tesoreria", "holding_tesoreria"],
  ["/holding/obligaciones", "holding_obligaciones"],
  ["/holding/estructura", "holding_estructura"],
  ["/holding", "holding_consolidado"],

  ["/auditoria", "auditoria"],

  ["/primeros-pasos", "primeros_pasos"],
  ["/dashboard", "dashboard"],
]

/** El ambito que corresponde a una ruta. Fuera del mapa, `generico`. */
export function ambitoDeRuta(pathname: string | null | undefined): Ambito {
  if (!pathname) return "generico"
  for (const [prefijo, ambito] of RUTAS) {
    if (pathname === prefijo || pathname.startsWith(prefijo + "/")) return ambito
  }
  return "generico"
}

/* ===========================================================================
   RESOLUCION
   =========================================================================== */

/**
 * Sustituye los nombres del fisco en una frase.
 *
 * La plantilla dice `{autoridad}` y aqui entra SUNAT, SAT, DGI o Receita
 * Federal segun el pais de la sociedad activa. Si no se sabe cual es, se
 * reescribe a una frase que no nombra a nadie: inventar «SUNAT» en la pantalla
 * de una empresa brasilena es peor que no decirlo.
 */
export function resolver(texto: string, autoridad?: string | null): string {
  if (!texto.includes("{autoridad}")) return texto
  if (autoridad) return texto.split("{autoridad}").join(autoridad)
  return texto.split("{autoridad}").join("el fisco")
}

/**
 * La voz de un ambito. Si el idioma no tiene catalogo, cae a espanol: una
 * frase en otro idioma se entiende; una clave cruda (`finanzas_pnl.carga`) no.
 */
export function voz(ambito: Ambito, idioma: Idioma = "es", autoridad?: string | null): Voz {
  const tabla = CATALOGO[idioma] ?? ES
  const base = tabla[ambito] ?? tabla.generico ?? ES.generico
  return {
    carga: resolver(base.carga, autoridad),
    vacio: {
      titulo: resolver(base.vacio.titulo, autoridad),
      pista: resolver(base.vacio.pista, autoridad),
    },
    error: resolver(base.error, autoridad),
  }
}

/** Atajo: de la ruta a la voz, sin pasar por el ambito. */
export function vozDeRuta(
  pathname: string | null | undefined,
  idioma: Idioma = "es",
  autoridad?: string | null,
): Voz {
  return voz(ambitoDeRuta(pathname), idioma, autoridad)
}

/* ===========================================================================
   MOMENTOS — los estados del sistema que no son de una pantalla
   =========================================================================== */

/**
 * Las frases del brandbook que no cuelgan de una seccion sino de un MOMENTO.
 * Son literales de la lamina 25, con una excepcion declarada: la alerta de
 * vencimiento lleva `{autoridad}` y `{cuando}` porque la frase del brandbook
 * («Ojo: la SUNAT te espera el viernes») nombra a un fisco concreto y un dia
 * concreto, y esta pantalla la ven cuatro paises.
 */
export const MOMENTOS: Record<Idioma, Record<string, string>> = {
  es: {
    primer_login: "Hola, soy Marty. De ahora en adelante, los números son mi problema.",
    arranque: "Marty está haciendo cuadrar todo…",
    cierre_listo: "Listo. Todo cuadra. Puedes respirar.",
    vencimiento: "Ojo: {autoridad} te espera {cuando}. Marty ya tiene todo listo.",
    guardado: "Guardado. Marty lo anotó.",
    sin_permiso: "Esto no te toca a ti. Marty tampoco insiste.",
    sesion_expirada: "Tu sesión venció. Nada se perdió: no había cambios sin guardar.",
    reintentando: "Marty lo está intentando otra vez…",
    cola_en_cero: "Cola en cero. Nada espera tu revisión; lo que sigue llegando lo voy clasificando.",
  },
  pt: {
    primer_login: "Olá, sou o Marty. De agora em diante, os números são problema meu.",
    arranque: "O Marty está fazendo tudo fechar…",
    cierre_listo: "Pronto. Tudo fecha. Você pode respirar.",
    vencimiento: "Atenção: {autoridad} te espera {cuando}. O Marty já deixou tudo pronto.",
    guardado: "Salvo. O Marty anotou.",
    sin_permiso: "Isto não é com você. O Marty também não insiste.",
    sesion_expirada: "Sua sessão expirou. Nada foi perdido: não havia alterações pendentes.",
    reintentando: "O Marty está tentando de novo…",
    cola_en_cero: "Fila zerada. Nada espera sua revisão; o que continuar chegando eu vou classificando.",
  },
  en: {
    primer_login: "Hi, I'm Marty. From now on, the numbers are my problem.",
    arranque: "Marty is making everything add up…",
    cierre_listo: "Done. Everything adds up. You can breathe.",
    vencimiento: "Heads up: {autoridad} expects you {cuando}. Marty already has it ready.",
    guardado: "Saved. Marty wrote it down.",
    sin_permiso: "This one isn't yours. Marty won't push either.",
    sesion_expirada: "Your session expired. Nothing was lost: there were no unsaved changes.",
    reintentando: "Marty is giving it another go…",
    cola_en_cero: "Queue at zero. Nothing is waiting on you; whatever keeps arriving, I'll keep classifying.",
  },
}

/** Una frase de momento, resuelta. `cuando` solo lo usa `vencimiento`. */
export function momento(
  clave: string,
  idioma: Idioma = "es",
  datos?: { autoridad?: string | null; cuando?: string | null },
): string {
  const tabla = MOMENTOS[idioma] ?? MOMENTOS.es
  const base = tabla[clave] ?? MOMENTOS.es[clave] ?? ""
  const conFisco = resolver(base, datos?.autoridad)
  return datos?.cuando
    ? conFisco.split("{cuando}").join(datos.cuando)
    : conFisco.split("{cuando}").join(idioma === "pt" ? "em breve" : idioma === "en" ? "soon" : "pronto")
}

/* ===========================================================================
   ERRORES — un 403 y un 500 no se resuelven igual, y no se cuentan igual.

   Estaban escritos en `DataState`, solo en espanol y con la voz del sistema
   («El backend rechazo los datos»). Viven aca por dos razones: para que la
   sociedad brasilena lea su error en portugues, y porque un mensaje de error es
   microcopy —el momento en el que mas importa que Marty no suba la voz—.

   Cada entrada dice QUE paso y COMO seguir. Ninguna dice «error inesperado»:
   eso no es un mensaje, es una renuncia.
   =========================================================================== */

export interface VozError {
  titulo: string
  ayuda: string
}

type ClaveError = "401" | "403" | "404" | "409" | "422" | "5xx" | "red" | "desconocido"

const ERRORES: Record<Idioma, Record<ClaveError, VozError>> = {
  es: {
    "401": {
      titulo: "Tu sesión venció",
      ayuda: "Vuelve a entrar. Nada se perdió: no había cambios sin guardar.",
    },
    "403": {
      titulo: "Esto no te toca a ti",
      ayuda: "Tu rol no incluye esta capacidad en la sociedad activa. Pídela a quien administra la cartera, o cambia de sociedad arriba.",
    },
    "404": {
      titulo: "Eso ya no está ahí",
      ayuda: "Puede haberse eliminado, o pertenecer a otra sociedad. Revisa el selector de arriba y vuelve a intentar.",
    },
    "409": {
      titulo: "El período está cerrado",
      ayuda: "Un período cerrado no se edita: la corrección va por asiento de reversión en un período posterior. Marty te lo prepara.",
    },
    "422": {
      titulo: "Algo en el formulario no cuadra",
      ayuda: "Marty ya lo encontró: mira el detalle de abajo, corrige ese campo y reintenta.",
    },
    "5xx": {
      titulo: "Falló el servidor, no tú",
      ayuda: "Reintenta. Si vuelve a pasar, reporta el incidente con la hora exacta: el identificador de la petición queda registrado.",
    },
    red: {
      titulo: "No llego al servidor",
      ayuda: "Revisa tu conexión. Lo que ves en pantalla es la última consulta que sí funcionó.",
    },
    desconocido: {
      titulo: "Algo no cuadra aquí",
      ayuda: "Marty ya lo anotó. Reintenta; si vuelve a fallar, reporta la hora y la pantalla.",
    },
  },
  pt: {
    "401": {
      titulo: "Sua sessão expirou",
      ayuda: "Entre de novo. Nada foi perdido: não havia alterações pendentes.",
    },
    "403": {
      titulo: "Isto não é com você",
      ayuda: "Seu perfil não tem essa permissão na empresa ativa. Peça a quem administra a carteira, ou troque de empresa acima.",
    },
    "404": {
      titulo: "Isso não está mais aí",
      ayuda: "Pode ter sido excluído, ou pertencer a outra empresa. Confira o seletor acima e tente de novo.",
    },
    "409": {
      titulo: "O período está fechado",
      ayuda: "Período fechado não se edita: a correção vai por lançamento de estorno em período posterior. O Marty prepara.",
    },
    "422": {
      titulo: "Algo no formulário não fecha",
      ayuda: "O Marty já encontrou: veja o detalhe abaixo, corrija esse campo e tente de novo.",
    },
    "5xx": {
      titulo: "Falhou o servidor, não você",
      ayuda: "Tente de novo. Se repetir, informe o incidente com a hora exata: o identificador da requisição fica registrado.",
    },
    red: {
      titulo: "Não alcanço o servidor",
      ayuda: "Confira sua conexão. O que está na tela é a última consulta que funcionou.",
    },
    desconocido: {
      titulo: "Algo não fecha aqui",
      ayuda: "O Marty já anotou. Tente de novo; se falhar outra vez, informe a hora e a tela.",
    },
  },
  en: {
    "401": {
      titulo: "Your session expired",
      ayuda: "Sign in again. Nothing was lost: there were no unsaved changes.",
    },
    "403": {
      titulo: "This one isn't yours",
      ayuda: "Your role doesn't include this capability for the active company. Ask whoever manages the portfolio, or switch company above.",
    },
    "404": {
      titulo: "That isn't there any more",
      ayuda: "It may have been deleted, or belong to another company. Check the selector above and try again.",
    },
    "409": {
      titulo: "The period is closed",
      ayuda: "A closed period isn't edited: the fix goes through a reversing entry in a later period. Marty will prepare it.",
    },
    "422": {
      titulo: "Something in the form doesn't add up",
      ayuda: "Marty already found it: check the detail below, fix that field and try again.",
    },
    "5xx": {
      titulo: "The server failed, not you",
      ayuda: "Try again. If it happens again, report the incident with the exact time: the request identifier is on record.",
    },
    red: {
      titulo: "I can't reach the server",
      ayuda: "Check your connection. What you see on screen is the last query that did work.",
    },
    desconocido: {
      titulo: "Something doesn't add up here",
      ayuda: "Marty already noted it. Try again; if it fails again, report the time and the screen.",
    },
  },
}

/**
 * La voz de un fallo.
 *
 * `estado` es el codigo HTTP cuando lo hay; `esDeRed` distingue «el servidor
 * dijo que no» de «no llegue al servidor», que son dos problemas de quien mira
 * muy distintos: uno se resuelve pidiendo un permiso y el otro mirando el wifi.
 */
export function vozDeError(
  opciones: { estado?: number | null; esDeRed?: boolean },
  idioma: Idioma = "es",
): VozError {
  const tabla = ERRORES[idioma] ?? ERRORES.es
  if (opciones.esDeRed) return tabla.red
  const estado = opciones.estado
  if (estado == null) return tabla.desconocido
  if (estado >= 500) return tabla["5xx"]
  const directo = String(estado) as ClaveError
  return tabla[directo] ?? tabla.desconocido
}

/* ===========================================================================
   APERTURA — lo que Marty dice CUANDO ABRES una seccion.

   No es lo mismo que `carga`. `carga` ocupa el hueco mientras viaja la
   consulta y desaparece; la apertura explica PARA QUE sirve esta pantalla y
   cual es el siguiente paso, y la lee sobre todo quien llega por primera vez.
   Antes esa explicacion vivia en la `description` del `PageHeader` de cada
   pantalla, escrita con la voz del sistema y sin traducir.

   Se muestra en una nota de Marty —fondo rayado, filete rojo— y se puede
   silenciar por seccion: quien pasa ocho horas en el diario no necesita que le
   expliquen el diario todos los dias. Ver `components/MartyNota.tsx`.
   =========================================================================== */

const APERTURAS: Record<Idioma, Record<Ambito, string>> = {
  es: {
    generico: "Estoy aquí. Dime qué necesitas ver.",
    dashboard:
      "Así va el mes. Si algo requiere tu atención, va en rojo — y solo una cosa a la vez.",
    primeros_pasos:
      "Te voy pidiendo lo que necesito, de una en una. No ordenes nada antes de mandármelo: yo leo cada archivo por dentro.",
    finanzas_pnl:
      "Este es el resultado del modelo de gestión. Si quieres el que se firma, está en Contabilidad › Estados.",
    finanzas_caja:
      "La caja de verdad: lo que entró y salió, no lo que se facturó. Las dos cosas casi nunca coinciden.",
    finanzas_revenue:
      "Tus ingresos, línea por línea de contrato. Una línea que ya terminó no cuenta como recurrente.",
    finanzas_costo_ventas:
      "Lo que cuesta entregar. Clasificar a una persona aquí o en gastos mueve tu margen bruto varios puntos.",
    finanzas_gastos:
      "El gasto operativo, por centro de costo. Lo que no tenga centro asignado lo dejo visible, no lo reparto solo.",
    finanzas_payroll:
      "La planilla y su costo real, aportes incluidos. Los aportes salen de caja cuando se pagan, no cuando se devengan.",
    finanzas_conciliacion:
      "Aquí decides qué factura entra al período y qué queda fuera. Lo que excluyas te pide un motivo: después alguien va a preguntar.",
    finanzas_hoja_vs_libro:
      "Finanzas contra Contabilidad, cuenta por cuenta. Una diferencia no es un error: es una decisión que alguien tomó y que conviene poder explicar.",
    revision:
      "Lo que no pude resolver solo. Cada fila trae mi propuesta, de dónde salió y cuánta confianza le tengo.",
    libro_diario:
      "Todos los asientos del período, en orden. Un período cerrado no se edita: se corrige por reversión.",
    libro_mayor: "El movimiento de una cuenta y su saldo. Elige la cuenta y el período.",
    libro_balance:
      "Sumas y saldos. Si no cuadra, la diferencia va grande y arriba: es el único número que importa en ese caso.",
    libro_analisis:
      "Una cuenta, hasta el fondo: de qué comprobante viene cada línea y quién la clasificó.",
    libro_comparar:
      "Los tres libros lado a lado. Donde divergen hay una norma que lo obliga, y te digo cuál.",
    estado_situacion:
      "El balance general, desde los asientos. Esto es lo que se entrega a un tercero, no el mayor.",
    estado_resultados:
      "El estado de resultados, desde los asientos. Cuadra contra el libro o te lo digo en la misma pantalla.",
    estado_flujo:
      "El flujo de efectivo, desde las cuentas de caja y bancos. La caja acumulada arranca donde terminó el período anterior.",
    maestros:
      "Tus datos base. Lo que registres aquí lo aplico desde el siguiente comprobante, no hacia atrás.",
    reglas:
      "Tus reglas de tratamiento. Sin reglas propias aplico la norma del país; una regla solo cambia lo que tú decidas cambiar.",
    declaraciones:
      "Las obligaciones del período. Yo las preparo; la firma la pone quien puede ponerla.",
    holding_consolidado:
      "Las cuatro sociedades como una sola. Antes del número te digo lo que tuve que suponer: eso cambia lo que el número significa.",
    holding_intercompania:
      "Lo que se facturan entre ustedes no es venta del grupo. Lo difícil no es eliminarlo: es encontrarlo, y para eso necesito que confirmes a quién reconozco.",
    holding_tesoreria:
      "Dónde está la caja, por sociedad. El total del grupo no te dice si México puede pagar su planilla; esta tabla sí.",
    auditoria:
      "Cada cambio con su autor, su fecha y su motivo. No lo puedo editar ni yo: la base rechaza cualquier intento de reescribir esta tabla.",
    holding_obligaciones:
      "Qué debe cada sociedad, ante quién y cuándo. Mira la última columna: te dice de cuáles sé armar el entregable y de cuáles solo sé la fecha.",
    holding_estructura:
      "Quién posee qué. Mientras no lo declares asumo el 100% — y si alguna filial tiene socios externos, el patrimonio del grupo te sale inflado.",
  },
  pt: {
    generico: "Estou aqui. Diga o que você precisa ver.",
    dashboard:
      "O mês está assim. Se algo precisa da sua atenção, vai em vermelho — e só uma coisa por vez.",
    primeros_pasos:
      "Vou pedindo o que preciso, uma coisa de cada vez. Não organize nada antes de me mandar: eu leio cada arquivo por dentro.",
    finanzas_pnl:
      "Este é o resultado do modelo de gestão. Se você quer o que se assina, está em Contabilidade › Demonstrações.",
    finanzas_caja:
      "O caixa de verdade: o que entrou e saiu, não o que foi faturado. As duas coisas quase nunca coincidem.",
    finanzas_revenue:
      "Suas receitas, linha por linha de contrato. Uma linha já encerrada não conta como recorrente.",
    finanzas_costo_ventas:
      "O que custa entregar. Classificar uma pessoa aqui ou em despesas move sua margem bruta em vários pontos.",
    finanzas_gastos:
      "A despesa operacional, por centro de custo. O que não tiver centro atribuído eu deixo visível, não rateio sozinho.",
    finanzas_payroll:
      "A folha e seu custo real, encargos incluídos. Os encargos saem do caixa quando são pagos, não quando são provisionados.",
    finanzas_conciliacion:
      "Aqui você decide que nota entra no período e que fica de fora. O que você excluir me pede um motivo: depois alguém vai perguntar.",
    finanzas_hoja_vs_libro:
      "Finanças contra Contabilidade, conta a conta. Uma diferença não é um erro: é uma decisão que alguém tomou e que conviria poder explicar.",
    revision:
      "O que não consegui resolver sozinho. Cada linha traz minha proposta, de onde saiu e quanta confiança eu tenho nela.",
    libro_diario:
      "Todos os lançamentos do período, em ordem. Período fechado não se edita: corrige-se por estorno.",
    libro_mayor: "O movimento de uma conta e seu saldo. Escolha a conta e o período.",
    libro_balance:
      "Somas e saldos. Se não fechar, a diferença vai grande e no alto: nesse caso é o único número que importa.",
    libro_analisis:
      "Uma conta, até o fundo: de que nota vem cada linha e quem a classificou.",
    libro_comparar:
      "Os três livros lado a lado. Onde divergem há uma norma que obriga, e eu digo qual.",
    estado_situacion:
      "O balanço patrimonial, a partir dos lançamentos. É isto que se entrega a um terceiro, não o razão.",
    estado_resultados:
      "A demonstração do resultado, a partir dos lançamentos. Fecha contra o livro ou eu aviso na mesma tela.",
    estado_flujo:
      "O fluxo de caixa, a partir das contas de caixa e bancos. O caixa acumulado começa onde terminou o período anterior.",
    maestros:
      "Seus dados base. O que você registrar aqui eu aplico a partir da próxima nota, não retroativamente.",
    reglas:
      "Suas regras de tratamento. Sem regras próprias eu aplico a norma do país; uma regra só muda o que você decidir mudar.",
    declaraciones:
      "As obrigações do período. Eu preparo; a assinatura é de quem pode assinar.",
    holding_consolidado:
      "As quatro empresas como uma só. Antes do número eu digo o que tive de supor: isso muda o que o número significa.",
    holding_intercompania:
      "O que vocês faturam entre si não é receita do grupo. O difícil não é eliminar: é encontrar, e para isso preciso que você confirme quem eu reconheço.",
    holding_tesoreria:
      "Onde está o caixa, por empresa. O total do grupo não diz se o México pode pagar a folha; esta tabela diz.",
    auditoria:
      "Cada alteração com seu autor, sua data e sua justificativa. Nem eu posso editar: o banco rejeita qualquer tentativa de reescrever esta tabela.",
    holding_obligaciones:
      "O que cada empresa deve, a quem e quando. Olhe a última coluna: ela diz de quais eu sei montar a entrega e de quais eu só sei a data.",
    holding_estructura:
      "Quem detém o quê. Enquanto você não declarar, eu assumo 100% — e se alguma controlada tem sócios externos, o patrimônio do grupo sai inflado.",
  },
  en: {
    generico: "I'm here. Tell me what you need to see.",
    dashboard:
      "Here's how the month is going. If something needs you, it's in red — and only one thing at a time.",
    primeros_pasos:
      "I ask for what I need, one thing at a time. Don't tidy anything up before sending it: I read every file myself.",
    finanzas_pnl:
      "This is the management result. If you want the one that gets signed, it's under Accounting › Statements.",
    finanzas_caja:
      "Real cash: what came in and went out, not what was invoiced. The two almost never match.",
    finanzas_revenue:
      "Your revenue, contract line by contract line. A line that already ended doesn't count as recurring.",
    finanzas_costo_ventas:
      "What delivery costs. Classifying a person here instead of in expenses moves your gross margin by points.",
    finanzas_gastos:
      "Operating expense, by cost centre. Anything without a centre assigned I leave visible — I don't allocate it on my own.",
    finanzas_payroll:
      "Payroll and its real cost, contributions included. Contributions leave cash when they're paid, not when they're accrued.",
    finanzas_conciliacion:
      "Here you decide which invoice belongs to the period and which doesn't. Whatever you exclude, I'll ask why: someone will ask later.",
    finanzas_hoja_vs_libro:
      "Finance against Accounting, account by account. A difference isn't an error: it's a decision someone made, and worth being able to explain.",
    revision:
      "What I couldn't settle on my own. Each row carries my proposal, where it came from, and how much I trust it.",
    libro_diario:
      "Every entry in the period, in order. A closed period isn't edited: it's corrected by reversal.",
    libro_mayor: "One account's movement and its balance. Pick the account and the period.",
    libro_balance:
      "Totals and balances. If it doesn't balance, the difference goes big and up top: it's the only number that matters then.",
    libro_analisis:
      "One account, all the way down: which document each line comes from and who classified it.",
    libro_comparar:
      "The three books side by side. Where they diverge, a rule requires it — and I'll tell you which.",
    estado_situacion:
      "The balance sheet, from the entries. This is what you hand to a third party, not the ledger.",
    estado_resultados:
      "The income statement, from the entries. It ties to the ledger, or I say so on this same screen.",
    estado_flujo:
      "The cash flow statement, from the cash and bank accounts. Opening cash starts where the last period ended.",
    maestros:
      "Your base data. Whatever you record here I apply from the next document on, not backwards.",
    reglas:
      "Your treatment rules. With none of your own I apply the country's standard; a rule only changes what you decide to change.",
    declaraciones:
      "The period's obligations. I prepare them; the signature belongs to whoever can sign.",
    holding_consolidado:
      "The four companies as one. Before the number I tell you what I had to assume: that changes what the number means.",
    holding_intercompania:
      "What you bill each other isn't group revenue. The hard part isn't eliminating it — it's finding it, and for that I need you to confirm who I recognise.",
    holding_tesoreria:
      "Where the cash is, by company. The group total won't tell you whether Mexico can make payroll; this table will.",
    auditoria:
      "Every change with its author, its date and its reason. Not even I can edit it: the database rejects any attempt to rewrite this table.",
    holding_obligaciones:
      "What each company owes, to whom and when. Look at the last column: it says which ones I can prepare and which ones I only know the date for.",
    holding_estructura:
      "Who owns what. Until you declare it I assume 100% — and if any subsidiary has outside shareholders, group equity comes out overstated.",
  },
}

/** Lo que Marty dice al abrir una seccion. Ver `APERTURAS`. */
export function apertura(
  ambito: Ambito,
  idioma: Idioma = "es",
  autoridad?: string | null,
): string {
  const tabla = APERTURAS[idioma] ?? APERTURAS.es
  return resolver(tabla[ambito] ?? tabla.generico, autoridad)
}
